import { type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, useVeEquipa } from '@/features/auth/AuthContext';
import { initials } from '@/lib/format';
import { Icon } from './ui/Icon';
import { PoweredByApexium } from './ui/PoweredByApexium';

interface TabDef {
  to: string;
  labelKey: string;
  icon: string;
}

/**
 * One shell, two shapes.
 *
 * Below `md` it is the phone app from the design: navy header, scrolling body,
 * fixed bottom tab bar. From `md` up it becomes a desktop web app — a navy
 * sidebar carries the same destinations, the header turns into a light title
 * bar, and the content column widens instead of staying a 560px strip in the
 * middle of a 1440px monitor. Same routes, same components; only the chrome
 * changes, so the crew on a phone and the encarregado on a laptop are looking
 * at the same app.
 */
export function AppLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const veEquipa = useVeEquipa();
  const location = useLocation();

  const tabs: TabDef[] = [
    { to: '/hoje', labelKey: veEquipa ? 'nav.dia' : 'nav.hoje', icon: 'today' },
    { to: '/mapa', labelKey: 'nav.mapa', icon: 'calendar_month' },
    { to: '/obras', labelKey: 'nav.obras', icon: 'apartment' },
    ...(veEquipa ? [{ to: '/equipa', labelKey: 'nav.equipa', icon: 'groups' }] : []),
    { to: '/perfil', labelKey: 'nav.perfil', icon: 'person' },
  ];

  const roleTag =
    user?.role === 'Administrador'
      ? t('header.admin')
      : user?.role === 'Encarregado'
        ? t('header.encarregado')
        : t('header.perfilTag');

  const avatar = user ? initials(user.displayName) : '—';

  return (
    <div className="min-h-dvh bg-conduril-surface md:flex">
      {/* ---- Sidebar (md and up) ---- */}
      <aside className="sticky top-0 hidden h-dvh w-56 shrink-0 flex-col bg-conduril px-3 py-6 md:flex lg:w-64 lg:px-4">
        <div className="px-2">
          <img src="/logo-white.svg" alt="Partes Diárias" className="h-10 w-auto" />
          <div className="mt-3 text-[15px] leading-tight text-white">
            <b className="font-bold">Partes</b> Diárias
          </div>
        </div>

        <nav className="mt-8 flex flex-1 flex-col gap-1">
          {tabs.map((tab) => {
            const active = location.pathname.startsWith(tab.to);
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-colors ${
                  active
                    ? 'bg-white text-conduril'
                    : 'text-conduril-soft hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon name={tab.icon} className="text-[20px]" />
                {t(tab.labelKey)}
              </NavLink>
            );
          })}
        </nav>

        <NavLink
          to="/perfil"
          className="mt-4 flex items-center gap-2.5 rounded-xl border border-white/15 bg-white/10 p-2.5 transition hover:bg-white/15"
        >
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white text-[11px] font-semibold text-conduril">
            {avatar}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12px] font-semibold text-white">
              {user?.displayName ?? '—'}
            </span>
            <span className="block text-[10px] uppercase tracking-[0.12em] text-conduril-soft">
              {roleTag}
            </span>
          </span>
        </NavLink>

        <PoweredByApexium tone="dark" className="mt-4" />
      </aside>

      {/* ---- Content column ---- */}
      <div className="flex min-h-dvh flex-1 flex-col md:min-w-0">
        {/* Phone header: navy, with the profile chip. */}
        <header className="shrink-0 bg-conduril px-4 pb-4 pt-safe md:hidden">
          <div className="flex items-start justify-between gap-3 pt-6">
            <div className="min-w-0">
              <img src="/logo-white.svg" alt="Partes Diárias" className="h-9 w-auto" />
              <h1 className="mt-3.5 text-[23px] font-semibold leading-tight tracking-tight text-white">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-1 text-[12px] leading-snug text-conduril-soft">{subtitle}</p>
              )}
            </div>
            <NavLink
              to="/perfil"
              className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/20 bg-white/10 py-1 pl-1 pr-3"
            >
              <span className="grid h-[25px] w-[25px] place-items-center rounded-full bg-white text-[10px] font-semibold text-conduril">
                {avatar}
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-white">
                {roleTag}
              </span>
            </NavLink>
          </div>
        </header>

        {/* Desktop header: the sidebar already carries the brand, so this is
            just the page title on a light bar. */}
        <header className="hidden shrink-0 border-b border-conduril-border bg-white px-6 py-5 md:block lg:px-10">
          <div className="mx-auto w-full max-w-6xl">
            <h1 className="text-[24px] font-semibold leading-tight tracking-tight text-conduril-ink">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1 text-[13px] leading-snug text-conduril-muted">{subtitle}</p>
            )}
          </div>
        </header>

        <main className="flex-1 px-4 pb-28 pt-4 md:px-6 md:pb-10 md:pt-6 lg:px-10">
          <div className="mx-auto w-full max-w-app md:max-w-6xl">
            {children}
            {/* On desktop the sidebar already carries this. */}
            <PoweredByApexium className="mt-10 md:hidden" />
          </div>
        </main>
      </div>

      {/* ---- Bottom tabs (phone only) ---- */}
      <nav className="fixed inset-x-0 bottom-0 z-30 mx-auto flex max-w-app gap-0.5 border-t border-conduril-border bg-white px-2 pt-2 pb-safe md:hidden">
        {tabs.map((tab) => {
          const active = location.pathname.startsWith(tab.to);
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={`flex flex-1 flex-col items-center gap-1.5 rounded-xl py-2 transition-colors ${
                active ? 'bg-conduril-tint text-conduril' : 'text-conduril-muted'
              }`}
            >
              <Icon name={tab.icon} className="text-[21px]" />
              <span className="text-[9px] font-semibold tracking-wide">{t(tab.labelKey)}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
